import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCalendarCheck,
  faCircleInfo,
  faRotate,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";
import "./FluxopayAnticipationPanel.css";

const unwrap = (response) => response.data;
const cents = (value) => Number(value || 0) / 100;
const money = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
const date = (value) =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "A definir";
const statusLabel = (status) =>
  ({
    simulated: "Simulada",
    pending: "Em análise",
    scheduled: "Agendada",
    credited: "Creditada",
    denied: "Negada",
    cancelled: "Cancelada",
    overdue: "Vencida",
    debited: "Baixada",
  })[status] || status;

const registrationLabel = (status) =>
  ({
    APPROVED: "Aprovado",
    PENDING: "Pendente",
    AWAITING_APPROVAL: "Em análise",
    REJECTED: "Reprovado",
  })[status] || status || "Não consultado";

export default function FluxopayAnticipationPanel() {
  const [receivables, setReceivables] = useState([]);
  const [anticipations, setAnticipations] = useState([]);
  const [configuration, setConfiguration] = useState(null);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [canAnticipate, setCanAnticipate] = useState(false);
  const [representative, setRepresentative] = useState(true);
  const [selected, setSelected] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [sandboxFallback, setSandboxFallback] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    const [receivableResponse, anticipationResponse] = await Promise.all([
      api.get("/api/fluxopay/receivables").then(unwrap),
      api.get("/api/fluxopay/anticipations").then(unwrap),
    ]);
    setReceivables(receivableResponse.data || []);
    setAnticipations(anticipationResponse.data || []);
    try {
      const config = await api
        .get("/api/fluxopay/anticipations/configuration")
        .then(unwrap);
      setConfiguration(config.configuration || {});
      setRegistrationStatus(config.registration_status || null);
      setDocuments(config.documents || []);
      setCanAnticipate(
        Boolean(config.capabilities?.anticipation),
      );
      setRepresentative(true);
    } catch (requestError) {
      if (requestError.response?.status === 403) setRepresentative(false);
      else throw requestError;
    }
  }, []);

  useEffect(() => {
    load().catch((err) =>
      setError(
        err.response?.data?.message ||
          "Não foi possível carregar as antecipações.",
      ),
    );
  }, [load]);

  const activeByOrder = useMemo(
    () => new Map(anticipations.map((item) => [item.payment_order_id, item])),
    [anticipations],
  );
  const eligible = receivables.filter(
    (order) =>
      order.provider === "asaas" &&
      order.payment_method === "credit_card" &&
      order.status === "paid" &&
      order.provider_charge_id?.startsWith("pay_") &&
      !["pending", "scheduled", "credited"].includes(
        activeByOrder.get(order.id)?.status,
      ),
  );

  const open = async (order) => {
    setSelected(order);
    setSimulation(null);
    setSandboxFallback(false);
    setConfirmed(false);
    setError("");
    setBusy(true);
    try {
      const result = await api
        .post(`/api/fluxopay/orders/${order.uuid}/anticipations/simulate`)
        .then(unwrap);
      setSimulation(result.anticipation);
    } catch (err) {
      if (err.response?.data?.sandbox_simulation_unavailable)
        setSandboxFallback(true);
      else
        setError(
          err.response?.data?.message ||
            "Não foi possível simular a antecipação.",
        );
    } finally {
      setBusy(false);
    }
  };

  const request = async () => {
    if (!selected || !confirmed) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/fluxopay/orders/${selected.uuid}/anticipations`, {
        simulation_uuid: simulation?.uuid || null,
        confirm: true,
      });
      setSelected(null);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Não foi possível solicitar a antecipação.",
      );
    } finally {
      setBusy(false);
    }
  };

  const toggleAutomatic = async (event) => {
    const enabled = event.target.checked;
    setBusy(true);
    setError("");
    try {
      const result = await api
        .put("/api/fluxopay/anticipations/configuration", {
          credit_card_automatic_enabled: enabled,
        })
        .then(unwrap);
      setConfiguration(
        result.configuration || { creditCardAutomaticEnabled: enabled },
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Não foi possível alterar a antecipação automática.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fxpa-grid">
      <section className="fxpay-card fxpa-card">
        <header>
          <div>
            <small>RECEBÍVEIS DE CARTÃO</small>
            <h3>Antecipação</h3>
          </div>
          <button
            className="fxpa-icon"
            onClick={() => load()}
            title="Atualizar"
          >
            <FontAwesomeIcon icon={faRotate} />
          </button>
        </header>
        {error && <p className="fxpa-error">{error}</p>}
        {representative && registrationStatus && (
          <section
            className={`fxpa-registration ${
              canAnticipate ? "is-approved" : "is-pending"
            }`}
          >
            <div className="fxpa-registration-title">
              <FontAwesomeIcon icon={faCircleInfo} />

              <div>
                <b>
                  {canAnticipate
                    ? "Subconta aprovada para antecipação"
                    : "Antecipação aguardando aprovação"}
                </b>

                <small>
                  {canAnticipate
                    ? "A situação cadastral da subconta está aprovada no Asaas."
                    : "A clínica pode continuar recebendo pagamentos, mas a antecipação será liberada somente após a aprovação cadastral."}
                </small>
              </div>
            </div>

            <div className="fxpa-registration-grid">
              <div>
                <small>Aprovação geral</small>
                <b>{registrationLabel(registrationStatus.general)}</b>
              </div>

              <div>
                <small>Dados comerciais</small>
                <b>
                  {registrationLabel(
                    registrationStatus.commercial_info,
                  )}
                </b>
              </div>

              <div>
                <small>Documentação</small>
                <b>
                  {registrationLabel(
                    registrationStatus.documentation,
                  )}
                </b>
              </div>

              <div>
                <small>Conta bancária</small>
                <b>
                  {registrationLabel(
                    registrationStatus.bank_account_info,
                  )}
                </b>
              </div>
            </div>

            {documents.length > 0 && (
              <div className="fxpa-documents">
                <strong>Pendências cadastrais</strong>

                {documents.map((document, index) => (
                  <div
                    className="fxpa-document"
                    key={document.id || `${document.type}-${index}`}
                  >
                    <div>
                      <b>{document.title}</b>
                      <small>
                        {registrationLabel(document.status)}
                        {document.description
                          ? ` · ${document.description}`
                          : ""}
                      </small>
                    </div>

                    {document.onboarding_url && (
                      <a
                        href={document.onboarding_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Enviar documento
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        {representative && configuration && (
          <label className="fxpa-auto">
            <div>
              <b>Antecipação automática</b>
              <small>
                O Asaas avaliará automaticamente os novos recebíveis elegíveis.
                {!canAnticipate && (
                  <small>
                    Disponível após a aprovação geral da subconta.
                  </small>
                )}
              </small>
            </div>
            <input
              type="checkbox"
              checked={Boolean(configuration.creditCardAutomaticEnabled)}
              disabled={busy || !canAnticipate}
              onChange={toggleAutomatic}
            />
          </label>
        )}
        {!representative && (
          <p className="fxpa-info">
            <FontAwesomeIcon icon={faCircleInfo} /> Somente o Representante
            Legal pode solicitar ou configurar antecipações.
          </p>
        )}
        <h4>Disponíveis para solicitar</h4>
        {eligible.length === 0 ? (
          <div className="fxpa-empty">
            <FontAwesomeIcon icon={faCalendarCheck} />
            <p>Nenhum recebível elegível neste momento.</p>
          </div>
        ) : (
          eligible.map((order) => (
            <article className="fxpa-row" key={order.id}>
              <div>
                <b>{order.external_id}</b>
                <small>
                  Crédito previsto em {date(order.provider_credit_at)}
                </small>
              </div>
              <strong>{money(cents(order.clinic_net_cents))}</strong>
              {representative && (
                <button
                  type="button"
                  disabled={!canAnticipate}
                  onClick={() => canAnticipate && open(order)}
                  title={
                    canAnticipate
                      ? "Simular antecipação"
                      : "Aguardando aprovação cadastral do Asaas"
                  }
                >
                  <FontAwesomeIcon icon={faBolt} />
                  {canAnticipate ? " Simular" : " Indisponível"}
                </button>
              )}
            </article>
          ))
        )}
      </section>
      <aside className="fxpay-card fxpa-card">
        <header>
          <div>
            <small>ACOMPANHAMENTO</small>
            <h3>Solicitações</h3>
          </div>
        </header>
        {anticipations.length === 0 ? (
          <p className="fxpa-muted">Nenhuma antecipação solicitada.</p>
        ) : (
          anticipations.map((item) => (
            <article className="fxpa-history" key={item.uuid}>
              <div>
                <b>{statusLabel(item.status)}</b>
                <small>
                  {item.order?.external_id || `Ordem #${item.payment_order_id}`}
                </small>
              </div>
              <span>
                {money(cents(item.net_amount_cents))}
                <small>
                  taxa {money(cents(item.fee_cents))} ·{" "}
                  {Number(item.effective_fee_percentage || 0).toLocaleString(
                    "pt-BR",
                    { maximumFractionDigits: 4 },
                  )}
                  %
                </small>
              </span>
            </article>
          ))
        )}
      </aside>
      {selected && (
        <div
          className="fxpa-backdrop"
          onMouseDown={() => !busy && setSelected(null)}
        >
          <section
            className="fxpa-modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <small>CONFIRMAÇÃO FINANCEIRA</small>
                <h3>Antecipar recebível</h3>
              </div>
              <button disabled={busy} onClick={() => setSelected(null)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            {busy && !simulation && !sandboxFallback ? (
              <p>Consultando elegibilidade e condições no Asaas...</p>
            ) : (
              <>
                <div className="fxpa-values">
                  <div>
                    <small>Recebível</small>
                    <b>{money(cents(selected.clinic_net_cents))}</b>
                  </div>
                  <div>
                    <small>Taxa de antecipação</small>
                    <b>
                      {simulation
                        ? money(cents(simulation.fee_cents))
                        : "Indisponível no Sandbox"}
                    </b>
                  </div>
                  <div className="total">
                    <small>Líquido antecipado</small>
                    <b>
                      {simulation
                        ? money(cents(simulation.net_amount_cents))
                        : "Será informado pelo Asaas"}
                    </b>
                  </div>
                </div>
                {sandboxFallback && (
                  <p className="fxpa-warning">
                    O Sandbox Asaas não simula antecipações. A solicitação de
                    teste pode ser enviada, mas taxa e líquido só serão
                    conhecidos na resposta/evento do provedor.
                  </p>
                )}
                <label className="fxpa-confirm">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  <span>
                    Confirmo que revisei as condições e autorizo a solicitação
                    ao Asaas.
                  </span>
                </label>
                <footer>
                  <button
                    className="fxpay-secondary"
                    disabled={busy}
                    onClick={() => setSelected(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    className="fxpay-primary"
                    disabled={
                      busy || !confirmed || (!simulation && !sandboxFallback)
                    }
                    onClick={request}
                  >
                    {busy ? "Enviando..." : "Confirmar antecipação"}
                  </button>
                </footer>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
